import { useEffect, useState, useCallback } from "react";
import type { QuizQuestion } from "../types/mmk";
import { getQuizItems } from "../axios/mmk";
import {
  loadQuizState,
  saveQuizState,
  clearQuizState,
  type AnsweredDetail,
} from "../utils/mmkPersistence";
import { toast } from "react-toastify";
import { trackMmkEvent } from "../utils/mmkAnalytics";

export function useMmkQuiz(
  scopeKey: string,
  markQuizWrong: (id: string) => Promise<void>,
  markAsMastered: (id: string) => Promise<void>,
) {
  const [isQuiz, setIsQuiz] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState<QuizQuestion[]>([]);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizIndex, setQuizIndex] = useState(0);
  const [quizCorrectIds, setQuizCorrectIds] = useState<string[]>([]);
  const [quizWrongIds, setQuizWrongIds] = useState<string[]>([]);
  const [quizAnsweredDetails, setQuizAnsweredDetails] = useState<
    Record<string, AnsweredDetail>
  >({});
  const [prefetchedQuizQuestions, setPrefetchedQuizQuestions] = useState<
    QuizQuestion[] | null
  >(null);

  const handleStartQuiz = useCallback(async () => {
    trackMmkEvent("Start Quiz");
    clearQuizState(scopeKey);
    setIsQuiz(true);
    setQuizCorrectIds([]);
    setQuizWrongIds([]);
    setQuizAnsweredDetails({});
    setQuizIndex(0);

    if (prefetchedQuizQuestions && prefetchedQuizQuestions.length > 0) {
      setQuizQuestions(prefetchedQuizQuestions);
      setQuizLoading(false);
      setPrefetchedQuizQuestions(null);
      return;
    }

    setQuizLoading(true);
    try {
      const res = await getQuizItems(5);
      setQuizQuestions(res.questions);
      if (res.questions.length === 0) {
        toast.info("Not enough cards for a quiz yet. Keep learning!");
      }
    } catch {
      toast.error("Failed to load quiz");
    } finally {
      setQuizLoading(false);
    }
  }, [prefetchedQuizQuestions, scopeKey]);

  const handleQuizAnswer = useCallback(
    async (itemId: string, correct: boolean, selectedOptionIndex: number) => {
      const key = `${itemId}::${quizIndex}`;
      setQuizAnsweredDetails((prev) => ({
        ...prev,
        [key]: { selectedOptionIndex, correct },
      }));
      if (correct) {
        setQuizCorrectIds((prev) => [...prev, itemId]);
        await markAsMastered(itemId);
      } else {
        setQuizWrongIds((prev) => [...prev, itemId]);
        await markQuizWrong(itemId);
      }
    },
    [quizIndex, markQuizWrong, markAsMastered],
  );

  const handleQuizNext = useCallback(() => {
    setQuizIndex((i) => i + 1);
  }, []);

  const handleExitQuiz = useCallback(() => {
    clearQuizState(scopeKey);
    setIsQuiz(false);
    setQuizQuestions([]);
    setQuizLoading(false);
    setQuizIndex(0);
    setQuizCorrectIds([]);
    setQuizWrongIds([]);
    setQuizAnsweredDetails({});
    setPrefetchedQuizQuestions(null);
  }, [scopeKey]);

  const buildCategoryBreakdown = useCallback(
    (questions: QuizQuestion[], correctIds: string[], wrongIds: string[]) => {
      const breakdown: Record<string, { correct: number; wrong: number }> = {};
      questions.forEach((q) => {
        const cat = q.category;
        if (!breakdown[cat]) breakdown[cat] = { correct: 0, wrong: 0 };
        if (correctIds.includes(q.item_id)) breakdown[cat].correct += 1;
        if (wrongIds.includes(q.item_id)) breakdown[cat].wrong += 1;
      });
      return breakdown;
    },
    [],
  );

  useEffect(() => {
    if (!scopeKey) return;
    const saved = loadQuizState(scopeKey);
    if (saved && saved.questions.length > 0) {
      setIsQuiz(true);
      setQuizQuestions(saved.questions);
      setQuizIndex(saved.index);
      setQuizCorrectIds(saved.correctIds);
      setQuizWrongIds(saved.wrongIds);
      setQuizAnsweredDetails(saved.answeredDetails || {});
      setQuizLoading(false);
      setPrefetchedQuizQuestions(null);
    }
  }, [scopeKey]);

  useEffect(() => {
    if (
      !scopeKey ||
      !isQuiz ||
      quizQuestions.length === 0 ||
      quizIndex >= quizQuestions.length
    ) {
      return;
    }
    saveQuizState(scopeKey, {
      questions: quizQuestions,
      index: quizIndex,
      correctIds: quizCorrectIds,
      wrongIds: quizWrongIds,
      answeredDetails: quizAnsweredDetails,
    });
  }, [
    scopeKey,
    isQuiz,
    quizQuestions,
    quizIndex,
    quizCorrectIds,
    quizWrongIds,
    quizAnsweredDetails,
  ]);

  useEffect(() => {
    if (
      scopeKey &&
      isQuiz &&
      quizQuestions.length > 0 &&
      quizIndex >= quizQuestions.length
    ) {
      clearQuizState(scopeKey);
    }
  }, [scopeKey, isQuiz, quizQuestions.length, quizIndex]);

  return {
    isQuiz,
    quizQuestions,
    quizLoading,
    quizIndex,
    quizCorrectIds,
    quizWrongIds,
    quizAnsweredDetails,
    prefetchedQuizQuestions,
    setPrefetchedQuizQuestions,
    handleStartQuiz,
    handleQuizAnswer,
    handleQuizNext,
    handleExitQuiz,
    buildCategoryBreakdown,
  };
}
